/**
 * GeoDirectory Marker Manager
 *
 * Handles fetching and displaying markers from the REST API.
 * Supports archive maps (post IDs from listings) and directory maps (dynamic loading).
 */

import { Popup } from './features/Popup.js';

export class MarkerManager {
	constructor(mapProvider, options = {}) {
		this.mapProvider = mapProvider;
		this.options = {
			restUrl: options.restUrl || window.geodir_map_params?.rest_url || '/wp-json/geodir/v3',
			restNonce: options.restNonce || window.geodir_map_params?.rest_nonce || '',
			...options
		};
		this.markers = [];
		this.markerData = {};
		this.markersByPostId = {}; // Map markers by post ID for quick lookup
		this.lastPostType = null;
		this.lastFilters = {};
		this.initialLoadComplete = false; // Track if initial map load has completed
	}

	/**
	 * Load markers for an archive map (from listing post IDs).
	 *
	 * @param {string} postType - The post type
	 * @returns {Promise}
	 */
	async loadArchiveMarkers(postType) {

		// Find all elements with data-post-id attribute (flexible selector)
		const listingItems = document.querySelectorAll('[data-post-id]');

		const postIds = Array.from(listingItems).map(item => {
			const postId = parseInt(item.dataset.postId);
			return postId;
		}).filter(id => id > 0);


		if (postIds.length === 0) {
			// No listings found - show no results and hide loading
			this.clearMarkers();
			this.showNoResults();
			this.mapProvider.hideLoading();
			return { items: [], total: 0 };
		}

		return this.fetchMarkers({
			post_type: postType,
			post: postIds
		});
	}

	/**
	 * Load markers for a directory map (all posts, filtered by terms/tags).
	 *
	 * @param {string} postType - The post type
	 * @param {object} filters - Filters (term[], tag[], search)
	 * @returns {Promise}
	 */
	async loadDirectoryMarkers(postType, filters = {}) {

		// Store for later use
		this.lastPostType = postType;
		this.lastFilters = filters;

		return this.fetchMarkers({
			post_type: postType,
			...filters
		});
	}

	/**
	 * Fetch markers from the REST API.
	 *
	 * @param {object} params - Query parameters
	 * @returns {Promise}
	 */
	async fetchMarkers(params) {
		const queryString = new URLSearchParams(params).toString();
		const url = `${this.options.restUrl}/markers?${queryString}`;


		// Show loading div (only for filter updates, not initial load)
		if (this.initialLoadComplete) {
			this.mapProvider.showLoading();
		}

		try {
			const response = await fetch(url, {
				headers: {
					'X-WP-Nonce': this.options.restNonce
				}
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const data = await response.json();

			if (data.items && data.items.length > 0) {
				this.displayMarkers(data.items, data);
				this.hideNoResults();
			} else {
				this.clearMarkers();
				this.showNoResults();
			}

			if (this.initialLoadComplete) {
				this.mapProvider.hideLoading();
			} else {
				this.initialLoadComplete = true;
				this.mapProvider.allowTileHideLoading = true;
				// If tiles already loaded (single marker on detail page), hide now
				requestAnimationFrame(() => {
					this.mapProvider.hideLoading();
				});
			}

			return data;
		} catch (error) {
			this.mapProvider.hideLoading();
			return { items: [], total: 0 };
		}
	}

	/**
	 * Display markers on the map.
	 *
	 * @param {Array} items - Array of marker data
	 * @param {object} response - Full API response
	 */
	displayMarkers(items, response) {

		// Clear existing markers
		this.clearMarkers();

		items.forEach((item, index) => {
			// Handle compact format: m = ID, lt = latitude, ln = longitude, t = title, i = icon ID
			if (!item.lt || !item.ln) {
				return;
			}

			try {
				// Get icon data
				const iconData = this.getMarkerIcon(item, response);

				// Build marker options
				const markerOptions = {
					lat: parseFloat(item.lt),
					lng: parseFloat(item.ln),
					title: item.t || '',
					draggable: false,
					data: item
				};

				// Add icon properties if available
				if (iconData) {
					markerOptions.icon = iconData.url;
					markerOptions.w = iconData.w;
					markerOptions.h = iconData.h;
				}

				// Add label field if exists (compact format: 'f' = field value)
				if (item.f) {
					markerOptions.label = item.f;
				}

				const marker = this.mapProvider.createMarker(markerOptions);

				// Only add click handler if OMS is NOT available
				// OMS will handle clicks for spiderfying
				if (!this.mapProvider.oms) {
					this.addMarkerClickHandler(marker, item);
				}

				this.markers.push(marker);
				this.markerData[item.m] = item;

				// Store marker by post ID for quick lookup (for marker interactions)
				this.markersByPostId[item.m] = marker;

			} catch (error) {
			}
		});

		// If OMS is available, listen to its click event for spiderfied markers
		if (this.mapProvider.oms) {
			this.setupOMSClickListener();
		}

		// Fit map to markers if available
		if (this.markers.length > 0) {
			this.fitMapToMarkers();
		}
	}

	/**
	 * Get the marker icon for a post.
	 *
	 * @param {object} item - Marker data
	 * @param {object} response - API response with baseurl/icons
	 * @returns {object|null} Icon data with url, w, h properties
	 */
	getMarkerIcon(item, response) {
		// Get icon ID from item (compact format: 'i' = icon ID)
		const iconId = item.i;

		if (!iconId || !response.icons || !response.icons[iconId]) {
			return null;
		}

		const iconData = response.icons[iconId];
		const baseUrl = response.baseurl || '';

		// Construct full icon URL
		const iconUrl = `${baseUrl}/${iconData.i}`;


		// Return simple object that GoogleMapProvider expects
		return {
			url: iconUrl,
			w: iconData.w,
			h: iconData.h
		};
	}

	/**
	 * Fit the map bounds to show all markers.
	 */
	fitMapToMarkers() {
		if (this.markers.length === 0) {
			return;
		}


		// Call the map provider's fitBounds method
		if (this.mapProvider && typeof this.mapProvider.fitBounds === 'function') {
			this.mapProvider.fitBounds(this.markers);
		} else {
		}
	}

	/**
	 * Clear all markers from the map.
	 */
	clearMarkers() {

		this.markers.forEach(marker => {
			// Remove marker from map based on provider
			if (marker && typeof marker.setMap === 'function') {
				// Google Maps
				marker.setMap(null);
			} else if (marker && typeof marker.remove === 'function') {
				// Leaflet
				marker.remove();
			}
		});

		this.markers = [];
		this.markerData = {};
		this.markersByPostId = {}; // Clear post ID lookup
	}

	/**
	 * Get a marker by post ID.
	 * Used by MarkerInteractions to animate markers when hovering over listings.
	 *
	 * @param {string|number} postId - The post ID
	 * @returns {object|null} The marker object or null if not found
	 */
	getMarkerByPostId(postId) {
		return this.markersByPostId[postId] || null;
	}

	/**
	 * Setup OverlappingMarkerSpiderfier click listener.
	 * This listens for clicks on spiderfied markers only.
	 */
	setupOMSClickListener() {
		if (!this.mapProvider.oms) {
			return;
		}

		// Clear any existing OMS listeners first
		this.mapProvider.oms.clearListeners('click');

		// Listen for clicks on spiderfied markers
		this.mapProvider.oms.addListener('click', (marker) => {

			// Get marker data from options
			const markerData = marker.options?.data;
			if (!markerData || !markerData.m) {
				return;
			}


			// Fetch and display popup
			Popup.fetchAndOpenPopup(
				this.mapProvider,
				marker,
				markerData.m, // Post ID
				{
					restUrl: this.options.restUrl,
					restNonce: this.options.restNonce
				}
			);
		});

	}

	/**
	 * Add click handler to marker to open popup.
	 *
	 * @param {object} marker - The marker object
	 * @param {object} item - Marker data from API
	 */
	addMarkerClickHandler(marker, item) {
		// Use provider-specific event listener
		if (this.mapProvider && typeof this.mapProvider.addMarkerListener === 'function') {
			this.mapProvider.addMarkerListener(marker, 'click', () => {

				// Fetch and display popup
				Popup.fetchAndOpenPopup(
					this.mapProvider,
					marker,
					item.m, // Post ID
					{
						restUrl: this.options.restUrl,
						restNonce: this.options.restNonce
					}
				);
			});
		} else {
		}
	}

	/**
	 * Filter markers by category term IDs.
	 * Re-fetches markers from API with the selected term filters.
	 * Preserves other active filters (search, tags, etc.).
	 *
	 * @param {Array} termIds - Array of term IDs to show
	 * @param {string} postType - Post type (optional, uses last known if not provided)
	 * @param {object} additionalFilters - Additional filters to preserve (search, tag, etc.)
	 */
	filterByTerms(termIds, postType = null, additionalFilters = {}) {

		// Get post type from last request or default
		if (!postType) {
			postType = this.lastPostType || window.geodir_post_type || 'gd_place';
		}

		// Build complete filters object, preserving additional filters
		const filters = {
			term: termIds,
			...additionalFilters
		};

		// Store for future reference
		this.lastPostType = postType;
		this.lastFilters = filters;

		// Use loadDirectoryMarkers for proper filter handling
		return this.loadDirectoryMarkers(postType, filters);
	}


	/**
	 * Show "No Results Found" message on map.
	 */
	showNoResults() {
		if (!this.mapProvider || !this.mapProvider.elementId) {
			return;
		}

		const noResultsDiv = document.getElementById(`${this.mapProvider.elementId}_map_nofound`);
		if (noResultsDiv) {
			noResultsDiv.style.display = '';
		}
	}

	/**
	 * Hide "No Results Found" message on map.
	 */
	hideNoResults() {
		if (!this.mapProvider || !this.mapProvider.elementId) {
			return;
		}

		const noResultsDiv = document.getElementById(`${this.mapProvider.elementId}_map_nofound`);
		if (noResultsDiv) {
			noResultsDiv.style.display = 'none';
		}
	}
}

